/** A colour as #RRGGBB, the only format the colour fields store. */
export const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

export const BLACK = '#000000';
export const WHITE = '#FFFFFF';

/**
 * WCAG thresholds for text. 4.5 : 1 is AA for normal text, 3 : 1 is AA for
 * large or bold text (buttons) — below that text is hard to read for everyone.
 */
export const CONTRAST_GOOD = 4.5;
export const CONTRAST_MIN = 3;

/** How readable text in one colour is on another. */
export type ContrastLevel = 'good' | 'limited' | 'poor';

export function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && HEX_COLOR.test(value);
}

/** WCAG relative luminance of a #RRGGBB colour (0 = black, 1 = white). */
export function luminance(hex: string): number {
  const channels = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  const linear = channels.map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
}

/** WCAG contrast ratio of two #RRGGBB colours (1 to 21), as the backend computes it. */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

export function contrastLevel(ratio: number): ContrastLevel {
  if (ratio >= CONTRAST_GOOD) return 'good';
  if (ratio >= CONTRAST_MIN) return 'limited';
  return 'poor';
}

/** Black or white, whichever reads better on the given background. */
export function readableTextColor(background: string): string {
  return contrastRatio(background, BLACK) >= contrastRatio(background, WHITE) ? BLACK : WHITE;
}
